import React from "react";
import styled from "styled-components";
import { injectIntl, defineMessages, FormattedMessage } from "react-intl";
import moment from "moment";
import DatePickerGroup from "../DatePickerGroup";
import MultiCheckBoxGroup from "../MultiCheckBoxGroup";
import { RESTRICTION_TYPE, WEEK_DAYS } from "../../../Common/constants";
import FieldErrorList from "../FieldErrorList";

const messages = defineMessages({
  closeTravelDate: {
    id: "app.periodConditions.closeTravelDate",
    description: "Close travel dates",
    defaultMessage: "Close travel dates",
  },
  startCloseTravelable: {
    id: "app.periodConditions.startCloseTravel",
    description: "Start date",
    defaultMessage: "Start date",
  },
  endCloseTravelable: {
    id: "app.periodConditions.endCloseTravel",
    description: "End date",
    defaultMessage: "End date",
  },
  endDateAfterStart: {
    id: "app.periodConditions.endDateAfterStart",
    description: "The end date must be greater than the start date and greater than today",
    defaultMessage: "The end date must be greater than the start date and greater than today",
  },
  weekDays: {
    id: "app.periodConditions.weekDays",
    description: "Week days (optional)",
    defaultMessage: "Weekdays (optional)",
  },
  weekDaysDescription: {
    id: "app.periodConditions.weekDaysDescription",
    description:
      "This rule will only apply if the time of the reservation occurs on any of the marked days (if all the fields are left empty it means they are enabled every day)",
    defaultMessage:
      "This rule will only apply if the time of the reservation occurs on any of the marked days (if all the fields are left empty it means they are enabled every day)",
  },
  monday: {
    id: "app.periodConditions.monday",
    description: "Monday",
    defaultMessage: "Monday",
  },
  tuesday: {
    id: "app.periodConditions.tuesday",
    description: "Tuesday",
    defaultMessage: "Tuesday",
  },
  wednesday: {
    id: "app.periodConditions.wednesday",
    description: "Wednesday",
    defaultMessage: "Wednesday",
  },
  thursday: {
    id: "app.periodConditions.thursday",
    description: "Thursday",
    defaultMessage: "Thursday",
  },
  friday: {
    id: "app.periodConditions.friday",
    description: "Friday",
    defaultMessage: "Friday",
  },
  saturday: {
    id: "app.periodConditions.saturday",
    description: "Saturday",
    defaultMessage: "Saturday",
  },
  sunday: {
    id: "app.periodConditions.sunday",
    description: "Sunday",
    defaultMessage: "Sunday",
  },
});

const Container = styled.div`
  a {
    cursor: pointer;
  }
`;

const DatePickerContainer = styled.div`
  fieldset {
    border-bottom: none;
  }

  .react-datepicker__input-container {
    width: 300px;
  }
`;

const DatePickerGroupStyled = styled.div`
  margin-top: 20px;
  fieldset {
    padding-bottom: 0;
    margin-bottom: 0;
  }
`;

const WeekdayPickerContainer = styled.div`
  margin-top: 20px;
  fieldset {
    padding-bottom: 0;
    margin-bottom: 10px;
  }
`;

const Message = styled.div`
  margin-bottom: 10px;
`;

const MessageTitle = styled.div`
  font-weight: 600;
`;

const StyledMultiCheckBoxGroup = styled(MultiCheckBoxGroup)`
  .multicheckbox {
    margin-top: 0;

    .multicheckboxes-container {
      margin-top: 0.5em;
      display: inline-flex;
    }
  }
`;

function CloseTravel({ field, formValues, onChange, intl, disabled, ...rest }) {
  const { restrictions } = formValues;
  const restrictionValues = (restrictions && restrictions[RESTRICTION_TYPE.CLOSE_TRAVEL]) || [];

  return (
    <Container>
      {restrictionValues.map((restrictionValue, key) => {
        return (
          <DatePickerContainer key={RESTRICTION_TYPE.CLOSE_TRAVEL + key}>
            <label>
              <FormattedMessage {...messages.startCloseTravelable} />
            </label>
            <DatePickerGroup
              disabled={disabled}
              value={restrictionValue && restrictionValue.startDate}
              field={{
                ...field,
                fieldName: `restrictions.${RESTRICTION_TYPE.CLOSE_TRAVEL}.${key}.startDate`,
                displayName: <FormattedMessage {...messages.startCloseTravelable} />,
                selectsStart: true,
              }}
              formValues={{
                startDate: restrictionValue && restrictionValue.startDate,
                endDate: restrictionValue && restrictionValue.endDate,
              }}
              onChange={onChange}
            />

            <DatePickerGroupStyled>
              <label>
                <FormattedMessage {...messages.endCloseTravelable} />
              </label>
              <DatePickerGroup
                disabled={disabled}
                value={restrictionValue && restrictionValue.endDate}
                field={{
                  ...field,
                  fieldName: `restrictions.${RESTRICTION_TYPE.CLOSE_TRAVEL}.${key}.endDate`,
                  displayName: <FormattedMessage {...messages.endCloseTravelable} />,
                  selectsEnd: true,
                }}
                formValues={{
                  startDate: restrictionValue && restrictionValue.startDate,
                  endDate: restrictionValue && restrictionValue.endDate,
                }}
                onChange={onChange}
              />
              <FieldErrorList
                errors={
                  restrictionValue &&
                  restrictionValue.endDate &&
                  (moment(restrictionValue.endDate).isBefore(restrictionValue.startDate) ||
                    moment(restrictionValue.endDate).isBefore(
                      moment(new Date()).format("YYYY-MM-DD")
                    )) && [intl.formatMessage(messages.endDateAfterStart)]
                }
              />
            </DatePickerGroupStyled>

            <WeekdayPickerContainer>
              <Message>
                <MessageTitle>
                  <FormattedMessage {...messages.weekDays} />
                </MessageTitle>
                <FormattedMessage {...messages.weekDaysDescription} />
              </Message>

              <StyledMultiCheckBoxGroup
                value={restrictionValue && restrictionValue.weekDays}
                field={{
                  disabled: disabled,
                  fieldName: `restrictions.${RESTRICTION_TYPE.CLOSE_TRAVEL}.${key}.weekDays`,
                  options: [
                    {
                      ticker: WEEK_DAYS.MONDAY,
                      name: <FormattedMessage {...messages.monday} />,
                    },
                    {
                      ticker: WEEK_DAYS.TUESDAY,
                      name: <FormattedMessage {...messages.tuesday} />,
                    },
                    {
                      ticker: WEEK_DAYS.WEDNESDAY,
                      name: <FormattedMessage {...messages.wednesday} />,
                    },
                    {
                      ticker: WEEK_DAYS.THURSDAY,
                      name: <FormattedMessage {...messages.thursday} />,
                    },
                    {
                      ticker: WEEK_DAYS.FRIDAY,
                      name: <FormattedMessage {...messages.friday} />,
                    },
                    {
                      ticker: WEEK_DAYS.SATURDAY,
                      name: <FormattedMessage {...messages.saturday} />,
                    },
                    {
                      ticker: WEEK_DAYS.SUNDAY,
                      name: <FormattedMessage {...messages.sunday} />,
                    },
                  ],
                }}
                onChange={onChange}
              />
            </WeekdayPickerContainer>
          </DatePickerContainer>
        );
      })}
    </Container>
  );
}

export default injectIntl(CloseTravel);
